import { createFileRoute, useRouter } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { and, eq } from "drizzle-orm";
import {
	KeyRound,
	Pencil,
	Plus,
	Shield,
	Trash2,
	User,
	UserCheck,
	X,
} from "lucide-react";
import { useState } from "react";
import { getDb } from "@/db";
import { accounts, sessions, users } from "@/db/schema";
import { getSession } from "@/lib/auth-session";

// ── Server functions ────────────────────────────────────────────

async function requireAdmin() {
	const session = await getSession();
	const role = (session?.user as { role?: string } | undefined)?.role;
	if (role !== "admin") throw new Error("Admin only");
	return session!;
}

const getUsers = createServerFn({ method: "GET" }).handler(async () => {
	await requireAdmin();
	return getDb().select().from(users);
});

const createUser = createServerFn({ method: "POST" })
	.inputValidator(
		(data: { name: string; email: string; password: string; role: string }) =>
			data,
	)
	.handler(async ({ data }) => {
		await requireAdmin();
		const { hashPassword } = await import("better-auth/crypto");
		const db = getDb();

		const id = crypto.randomUUID();
		const now = new Date();

		await db.insert(users).values({
			id,
			name: data.name,
			email: data.email,
			role: data.role,
			createdAt: now,
			updatedAt: now,
		});

		const hashedPassword = await hashPassword(data.password);
		await db.insert(accounts).values({
			id: crypto.randomUUID(),
			accountId: id,
			providerId: "credential",
			userId: id,
			password: hashedPassword,
			createdAt: now,
			updatedAt: now,
		});
	});

const updateUser = createServerFn({ method: "POST" })
	.inputValidator(
		(data: { userId: string; name: string; email: string; role: string }) =>
			data,
	)
	.handler(async ({ data }) => {
		const session = await requireAdmin();
		if (data.userId === session.user?.id && data.role !== "admin") {
			throw new Error("Cannot demote yourself");
		}

		await getDb()
			.update(users)
			.set({
				name: data.name,
				email: data.email,
				role: data.role,
				updatedAt: new Date(),
			})
			.where(eq(users.id, data.userId));
	});

const setUserPassword = createServerFn({ method: "POST" })
	.inputValidator((data: { userId: string; password: string }) => data)
	.handler(async ({ data }) => {
		await requireAdmin();
		const { hashPassword } = await import("better-auth/crypto");
		const hashedPassword = await hashPassword(data.password);

		const db = getDb();
		const existing = await db
			.select({ id: accounts.id })
			.from(accounts)
			.where(
				and(
					eq(accounts.userId, data.userId),
					eq(accounts.providerId, "credential"),
				),
			)
			.limit(1);

		if (existing.length > 0) {
			await db
				.update(accounts)
				.set({ password: hashedPassword, updatedAt: new Date() })
				.where(eq(accounts.id, existing[0].id));
		} else {
			await db.insert(accounts).values({
				id: crypto.randomUUID(),
				accountId: data.userId,
				providerId: "credential",
				userId: data.userId,
				password: hashedPassword,
				createdAt: new Date(),
				updatedAt: new Date(),
			});
		}
	});

const deleteUser = createServerFn({ method: "POST" })
	.inputValidator((data: { userId: string }) => data)
	.handler(async ({ data }) => {
		const session = await requireAdmin();
		if (data.userId === session.user?.id) {
			throw new Error("Cannot delete yourself");
		}

		const db = getDb();
		await db.delete(sessions).where(eq(sessions.userId, data.userId));
		await db.delete(accounts).where(eq(accounts.userId, data.userId));
		await db.delete(users).where(eq(users.id, data.userId));
	});

// ── Route ───────────────────────────────────────────────────────

export const Route = createFileRoute("/admin/users")({
	loader: () => getUsers(),
	component: UsersPage,
});

const roleConfig = {
	admin: { icon: Shield, color: "text-primary", bg: "bg-primary/20" },
	operator: { icon: UserCheck, color: "text-success", bg: "bg-success/20" },
	public: { icon: User, color: "text-muted-foreground", bg: "bg-muted" },
} as const;

// ── Component ───────────────────────────────────────────────────

type ModalState =
	| { type: "none" }
	| { type: "create" }
	| {
			type: "edit";
			user: { id: string; name: string; email: string; role: string };
	  }
	| { type: "password"; userId: string; userName: string }
	| { type: "delete"; userId: string; userName: string };

function UsersPage() {
	const userList = Route.useLoaderData();
	const router = useRouter();
	const [modal, setModal] = useState<ModalState>({ type: "none" });
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const reload = () => router.invalidate();

	return (
		<div className="min-h-screen bg-background p-6">
			<div className="max-w-3xl mx-auto">
				<div className="flex items-center justify-between mb-8">
					<h1 className="text-4xl text-foreground">User Management</h1>
					<button
						type="button"
						onClick={() => {
							setError(null);
							setModal({ type: "create" });
						}}
						className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-accent text-primary-foreground font-bold rounded-lg transition-colors text-sm"
					>
						<Plus size={16} />
						Add User
					</button>
				</div>

				<div className="space-y-3">
					{userList.map((user) => {
						const config =
							roleConfig[user.role as keyof typeof roleConfig] ||
							roleConfig.public;
						const Icon = config.icon;
						return (
							<div
								key={user.id}
								className="flex items-center justify-between bg-card rounded-xl px-5 py-4"
							>
								<div className="flex items-center gap-3">
									<div
										className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center`}
									>
										<Icon size={18} className={config.color} />
									</div>
									<div>
										<p className="text-foreground font-bold">
											{user.name}
										</p>
										<p className="text-muted-foreground text-sm">
											{user.email}
										</p>
									</div>
								</div>
								<div className="flex items-center gap-2">
									<span
										className={`text-xs px-2 py-0.5 rounded font-bold uppercase ${config.bg} ${config.color}`}
									>
										{user.role}
									</span>
									<button
										type="button"
										onClick={() => {
											setError(null);
											setModal({
												type: "edit",
												user: {
													id: user.id,
													name: user.name,
													email: user.email,
													role: user.role,
												},
											});
										}}
										className="p-1.5 hover:bg-secondary rounded-lg transition-colors text-muted-foreground hover:text-foreground"
										title="Edit user"
									>
										<Pencil size={14} />
									</button>
									<button
										type="button"
										onClick={() => {
											setError(null);
											setModal({
												type: "password",
												userId: user.id,
												userName: user.name,
											});
										}}
										className="p-1.5 hover:bg-secondary rounded-lg transition-colors text-muted-foreground hover:text-foreground"
										title="Change password"
									>
										<KeyRound size={14} />
									</button>
									<button
										type="button"
										onClick={() => {
											setError(null);
											setModal({
												type: "delete",
												userId: user.id,
												userName: user.name,
											});
										}}
										className="p-1.5 hover:bg-secondary rounded-lg transition-colors text-muted-foreground hover:text-destructive"
										title="Delete user"
									>
										<Trash2 size={14} />
									</button>
								</div>
							</div>
						);
					})}
				</div>
			</div>

			{/* Modals */}
			{modal.type === "create" && (
				<CreateUserModal
					loading={loading}
					error={error}
					onClose={() => setModal({ type: "none" })}
					onSubmit={async (data) => {
						setLoading(true);
						setError(null);
						try {
							await createUser({ data });
							setModal({ type: "none" });
							reload();
						} catch (e) {
							setError(
								e instanceof Error ? e.message : "Failed to create user",
							);
						} finally {
							setLoading(false);
						}
					}}
				/>
			)}

			{modal.type === "edit" && (
				<EditUserModal
					user={modal.user}
					loading={loading}
					error={error}
					onClose={() => setModal({ type: "none" })}
					onSubmit={async (data) => {
						setLoading(true);
						setError(null);
						try {
							await updateUser({ data });
							setModal({ type: "none" });
							reload();
						} catch (e) {
							setError(
								e instanceof Error ? e.message : "Failed to update user",
							);
						} finally {
							setLoading(false);
						}
					}}
				/>
			)}

			{modal.type === "password" && (
				<PasswordModal
					userName={modal.userName}
					loading={loading}
					error={error}
					onClose={() => setModal({ type: "none" })}
					onSubmit={async (password) => {
						setLoading(true);
						setError(null);
						try {
							await setUserPassword({
								data: { userId: modal.userId, password },
							});
							setModal({ type: "none" });
						} catch (e) {
							setError(
								e instanceof Error
									? e.message
									: "Failed to change password",
							);
						} finally {
							setLoading(false);
						}
					}}
				/>
			)}

			{modal.type === "delete" && (
				<DeleteModal
					userName={modal.userName}
					loading={loading}
					error={error}
					onClose={() => setModal({ type: "none" })}
					onConfirm={async () => {
						setLoading(true);
						setError(null);
						try {
							await deleteUser({ data: { userId: modal.userId } });
							setModal({ type: "none" });
							reload();
						} catch (e) {
							setError(
								e instanceof Error ? e.message : "Failed to delete user",
							);
						} finally {
							setLoading(false);
						}
					}}
				/>
			)}
		</div>
	);
}

// ── Modal components ────────────────────────────────────────────

function ModalShell({
	title,
	onClose,
	children,
}: {
	title: string;
	onClose: () => void;
	children: React.ReactNode;
}) {
	return (
		<>
			<div
				className="fixed inset-0 bg-black/50 z-40"
				onClick={onClose}
			/>
			<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
				<div className="bg-card rounded-xl shadow-2xl w-full max-w-md">
					<div className="flex items-center justify-between px-5 py-4 border-b border-border">
						<h2 className="text-lg font-bold text-foreground">
							{title}
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="p-1 hover:bg-secondary rounded-lg transition-colors"
						>
							<X size={18} />
						</button>
					</div>
					<div className="px-5 py-4">{children}</div>
				</div>
			</div>
		</>
	);
}

function FormError({ error }: { error: string | null }) {
	if (!error) return null;
	return <p className="text-destructive text-sm">{error}</p>;
}

function CreateUserModal({
	loading,
	error,
	onClose,
	onSubmit,
}: {
	loading: boolean;
	error: string | null;
	onClose: () => void;
	onSubmit: (data: {
		name: string;
		email: string;
		password: string;
		role: string;
	}) => void;
}) {
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [role, setRole] = useState("public");

	return (
		<ModalShell title="Add User" onClose={onClose}>
			<form
				className="space-y-3"
				autoComplete="off"
				onSubmit={(e) => {
					e.preventDefault();
					onSubmit({ name, email, password, role });
				}}
			>
				<FieldInput label="Name" value={name} onChange={setName} required autoComplete="off" />
				<FieldInput
					label="Email"
					type="email"
					value={email}
					onChange={setEmail}
					required
					autoComplete="off"
				/>
				<FieldInput
					label="Password"
					type="password"
					value={password}
					onChange={setPassword}
					required
					minLength={6}
					autoComplete="new-password"
				/>
				<FieldSelect label="Role" value={role} onChange={setRole} />
				<FormError error={error} />
				<div className="flex justify-end gap-2 pt-2">
					<button
						type="button"
						onClick={onClose}
						className="px-4 py-2 text-sm bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors"
					>
						Cancel
					</button>
					<button
						type="submit"
						disabled={loading}
						className="px-4 py-2 text-sm bg-primary hover:bg-accent text-primary-foreground font-bold rounded-lg transition-colors disabled:opacity-50"
					>
						{loading ? "Creating..." : "Create User"}
					</button>
				</div>
			</form>
		</ModalShell>
	);
}

function EditUserModal({
	user,
	loading,
	error,
	onClose,
	onSubmit,
}: {
	user: { id: string; name: string; email: string; role: string };
	loading: boolean;
	error: string | null;
	onClose: () => void;
	onSubmit: (data: {
		userId: string;
		name: string;
		email: string;
		role: string;
	}) => void;
}) {
	const [name, setName] = useState(user.name);
	const [email, setEmail] = useState(user.email);
	const [role, setRole] = useState(user.role);

	return (
		<ModalShell title="Edit User" onClose={onClose}>
			<form
				className="space-y-3"
				onSubmit={(e) => {
					e.preventDefault();
					onSubmit({ userId: user.id, name, email, role });
				}}
			>
				<FieldInput label="Name" value={name} onChange={setName} required />
				<FieldInput
					label="Email"
					type="email"
					value={email}
					onChange={setEmail}
					required
				/>
				<FieldSelect label="Role" value={role} onChange={setRole} />
				<FormError error={error} />
				<div className="flex justify-end gap-2 pt-2">
					<button
						type="button"
						onClick={onClose}
						className="px-4 py-2 text-sm bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors"
					>
						Cancel
					</button>
					<button
						type="submit"
						disabled={loading}
						className="px-4 py-2 text-sm bg-primary hover:bg-accent text-primary-foreground font-bold rounded-lg transition-colors disabled:opacity-50"
					>
						{loading ? "Saving..." : "Save Changes"}
					</button>
				</div>
			</form>
		</ModalShell>
	);
}

function PasswordModal({
	userName,
	loading,
	error,
	onClose,
	onSubmit,
}: {
	userName: string;
	loading: boolean;
	error: string | null;
	onClose: () => void;
	onSubmit: (password: string) => void;
}) {
	const [password, setPassword] = useState("");
	const [confirm, setConfirm] = useState("");
	const mismatch = confirm.length > 0 && password !== confirm;

	return (
		<ModalShell title={`Change Password — ${userName}`} onClose={onClose}>
			<form
				className="space-y-3"
				onSubmit={(e) => {
					e.preventDefault();
					if (!mismatch) onSubmit(password);
				}}
			>
				<FieldInput
					label="New Password"
					type="password"
					value={password}
					onChange={setPassword}
					required
					minLength={6}
				/>
				<FieldInput
					label="Confirm Password"
					type="password"
					value={confirm}
					onChange={setConfirm}
					required
				/>
				{mismatch && (
					<p className="text-destructive text-sm">
						Passwords do not match
					</p>
				)}
				<FormError error={error} />
				<div className="flex justify-end gap-2 pt-2">
					<button
						type="button"
						onClick={onClose}
						className="px-4 py-2 text-sm bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors"
					>
						Cancel
					</button>
					<button
						type="submit"
						disabled={loading || mismatch}
						className="px-4 py-2 text-sm bg-primary hover:bg-accent text-primary-foreground font-bold rounded-lg transition-colors disabled:opacity-50"
					>
						{loading ? "Saving..." : "Set Password"}
					</button>
				</div>
			</form>
		</ModalShell>
	);
}

function DeleteModal({
	userName,
	loading,
	error,
	onClose,
	onConfirm,
}: {
	userName: string;
	loading: boolean;
	error: string | null;
	onClose: () => void;
	onConfirm: () => void;
}) {
	return (
		<ModalShell title="Delete User" onClose={onClose}>
			<div className="space-y-4">
				<p className="text-foreground">
					Are you sure you want to delete{" "}
					<span className="font-bold">{userName}</span>? This cannot be
					undone.
				</p>
				<FormError error={error} />
				<div className="flex justify-end gap-2">
					<button
						type="button"
						onClick={onClose}
						className="px-4 py-2 text-sm bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={onConfirm}
						disabled={loading}
						className="px-4 py-2 text-sm bg-destructive hover:bg-destructive/80 text-destructive-foreground font-bold rounded-lg transition-colors disabled:opacity-50"
					>
						{loading ? "Deleting..." : "Delete User"}
					</button>
				</div>
			</div>
		</ModalShell>
	);
}

// ── Shared form fields ──────────────────────────────────────────

function FieldInput({
	label,
	type = "text",
	value,
	onChange,
	required,
	minLength,
	autoComplete,
}: {
	label: string;
	type?: string;
	value: string;
	onChange: (v: string) => void;
	required?: boolean;
	minLength?: number;
	autoComplete?: string;
}) {
	return (
		<div>
			<label className="block text-xs font-bold text-muted-foreground uppercase mb-1">
				{label}
			</label>
			<input
				type={type}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				required={required}
				minLength={minLength}
				autoComplete={autoComplete}
				className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
			/>
		</div>
	);
}

function FieldSelect({
	label,
	value,
	onChange,
}: {
	label: string;
	value: string;
	onChange: (v: string) => void;
}) {
	return (
		<div>
			<label className="block text-xs font-bold text-muted-foreground uppercase mb-1">
				{label}
			</label>
			<select
				value={value}
				onChange={(e) => onChange(e.target.value)}
				className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
			>
				<option value="admin">Admin — full access, can manage users and all settings</option>
				<option value="operator">Operator — can run races and manage events, but not users</option>
				<option value="public">Public — can register cars and vote only</option>
			</select>
		</div>
	);
}
